export const API_HOST =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * Tenant-scoped:
 *   http://localhost:8000/clients/:tenant/api
 * Public (no tenant):
 *   http://localhost:8000/api
 */
const normalizeTenant = (tenant?: string) => {
  const t = (tenant || '').trim();
  if (!t || t === 'public') return null;
  return t;
};

export const tenantApiBase = (tenant?: string) => {
  const normalizeTenantCode = normalizeTenant(tenant);
  return normalizeTenantCode
    ? `${API_HOST}/clients/${encodeURIComponent(normalizeTenantCode)}/api`
    : `${API_HOST}/api`;
};

const usersBase = (tenant?: string) => `${tenantApiBase(tenant)}/users`;

export const USER_ROUTES = {
  // -------- USERS (tenant-based) ----------
  ME: (tenant?: string) => `${usersBase(tenant)}/profile`,
  UPDATE_USER_PROFILE: (tenant: string) =>
    `${usersBase(tenant)}/profile/update`,

  EXPERIENCES: (tenant: string) => `${usersBase(tenant)}/experience`,
  UPDATE_USER_PROFILE_PIC: (tenant: string) =>
    `${usersBase(tenant)}/profile/profile_picture/update`,
  DELETE_ACCOUNT: `${API_HOST}/api/users/account/`,
  REQUEST_DELETE_ACCOUNT_TOKEN: `${API_HOST}/api/users/account/request-delete-token/`,

  ADD_EXPERIENCE: (tenant: string) => `${usersBase(tenant)}/experience/add`,
  UPDATE_EXPERIENCE: (tenant: string, id: number) =>
    `${usersBase(tenant)}/experience/${id}/update`,
  DELETE_EXPERIENCE: (tenant: string, id: number) =>
    `${usersBase(tenant)}/experience/${id}/delete`,

  GET_USER_EDUCATIONS: (tenant: string) => `${usersBase(tenant)}/education`,
  ADD_USER_EDUCATIONS: (tenant: string) => `${usersBase(tenant)}/education/add`,
  UPDATE_USER_EDUCATIONS: (tenant: string, id: number) =>
    `${usersBase(tenant)}/education/${id}/update`,
  DELETE_USER_EDUCATIONS: (tenant: string, id: number) =>
    `${usersBase(tenant)}/education/${id}/delete`,

  GET_USER_CERTIFICATIONS: (tenant: string) =>
    `${usersBase(tenant)}/certificates`,
  ADD_CERTIFICATION: (tenant: string) =>
    `${usersBase(tenant)}/certificates/add`,
  DELETE_USER_CERTIFICATION: (tenant: string, id: number) =>
    `${usersBase(tenant)}/certificates/${id}/delete`,
  UPDATE_USER_CERTIFICATION: (tenant: string, id: number) =>
    `${usersBase(tenant)}/certificates/${id}/update`,

  GET_USER_SKILLS: (tenant: string, userId: string) =>
    `${usersBase(tenant)}/user-skill-list/?user_id=${encodeURIComponent(userId)}`,
  USER_PROFILE_DETAIL: (tenant: string, userId: string) =>
    `${tenantApiBase(tenant)}/users/users-profile/${userId}/`,
  ADD_USER_SKILL: (tenant: string) => `${usersBase(tenant)}/skills/add`,
  USER_SKILL_RATING: (tenant: string, userId: string) =>
    `${usersBase(tenant)}/user-skill-rating/?user=${encodeURIComponent(userId)}`,
  UPDATE_USER_SKILL_RATING: (tenant: string, id: number) =>
    `${usersBase(tenant)}/user-skill-rating/${id}/`,
  DELETE_USER_SKILL_RATING: (tenant: string, id: number) =>
    `${usersBase(tenant)}/skills/remove/${id}`,

  // -------- ORG / CLIENT (tenant-based) ----------
  GET_SCOPE: (tenant: string) => `${tenantApiBase(tenant)}/skills/scope/`,
  GET_SKILL_FRAMEWORK: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/skill-framework/`,
  GET_PUBLIC_SKILL_FRAMEWORK: (tenant: string) =>
    `${tenantApiBase(tenant)}/skills/skill-framework/`,
  GET_SKILL_FRAMEWORK_BY_ID: (tenant: string, id: string | number) =>
    `${tenantApiBase(tenant)}/client/skill-framework/${id}/`,
  DUPLICATE_SKILL_FRAMEWORK: (tenant: string, id: string | number) =>
    `${tenantApiBase(tenant)}/client/skill-framework/${id}/duplicate_skills/`,
  PARSE_SKILLS_FRAMEWORK: (tenant: string) =>
    `${tenantApiBase(tenant)}/skills/parse-skills-framework`,
  BULK_CREATE_SKILL_FRAMEWORK: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/skill-framework/bulk-create/`,
  GET_PUBLIC_SKILL_FRAMEWORK_TYPES: (tenant: string) =>
    `${tenantApiBase(tenant)}/skills/skill-framework-type/`,
  GET_ORG_SKILL_FRAMEWORK_TYPES: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/skill-framework-type/`,
  ORGANIZATION_CLIENT_PROFILE_UPDATE: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/profile/update`,
  ORGANIZATION_TEMPLATE_UPDATE: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/template/update`,

  /**
   * GLOBAL (NOT tenant-based)
   */
  GET_USER_ORGANIZATIONS: `${API_HOST}/api/users/user-client-details/`,
  ADD_USER_ORGANIZATION_GLOBAL: `${API_HOST}/api/clients/create-clients-validation-less/`,

  // STEP 1: Landing page (cards grid)
  GET_INTERNAL_PROJECTS_OVERVIEW: (tenant: string, board?: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-projects/?board=${board ?? ''}`,
  GET_INTERNAL_PROJECTS_ASSIGNED: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/all?project_type=1&board=`,
  // STEP 2: Board → project list
  GET_INTERNAL_PROJECT_DETAILS_BY_PROJECT_ID: (
    tenant: string,
    projectId: number
  ) =>
    `${tenantApiBase(tenant)}/departments/internal-school-projects/${projectId}`,
  CREATE_INTERNAL_PROJECT: (tenant: string) =>
    `${tenantApiBase(tenant)}/departments/internal-school-projects/`,
  UPDATE_INTERNAL_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-projects/${projectId}/`,
  DELETE_INTERNAL_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-projects/${projectId}/`,
  UPDATE_INTERNAL_PROJECT_THUMBNAIL: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-projects/${projectId}/`,
  DUPLICATE_INTERNAL_PROJECT: (tenant: string) =>
    `${tenantApiBase(tenant)}/departments/duplicate-project/`,
  GET_ALL_CLIENT_USER_LIST: (tenant: string) =>
    `${tenantApiBase(tenant)}/users/client-users-list/`,
  ADD_CLIENT_USER: (tenant: string) =>
    `${tenantApiBase(tenant)}/users/add-users/`,
  UPDATE_CLIENT_USER_ROLES: (tenant: string, roleId: number) =>
    `${tenantApiBase(tenant)}/users/update-user-roles/${roleId}/`,
  DELETE_CLIENT_USER: (tenant: string, userId: string) =>
    `${tenantApiBase(tenant)}/users/delete_user/${encodeURIComponent(userId)}`,
  ADD_CLIENT_USERS_BULK: (tenant: string) =>
    `${tenantApiBase(tenant)}/users/add-users-in-bulk/`,
  ASSIGN_INTERNAL_PROJECT: (tenant: string) =>
    `${tenantApiBase(tenant)}/departments/assign-internal-school-projects/`,
  GET_INTERNAL_PROJECTS_DETAILED_APPLIED: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/detailed-applied/`,
  GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_PROJECT_ID: (
    tenant: string,
    projectId: number
  ) =>
    `${tenantApiBase(tenant)}/assigned-projects/detailed-applied/${projectId}/`,
  //  External Projects endpoints start from here
  GET_EXTERNAL_PROJECTS: (tenant: string) =>
    `${tenantApiBase(tenant)}/projects/all`,
  GET_EXTERNAL_PROJECT_DETAILS: (tenant: string, id: number) =>
    `${tenantApiBase(tenant)}/projects/${id}`,
  // FOr non-comany external
  GET_EXTERNAL_AVAILABLE_PROJECTS: () =>
    `${tenantApiBase()}/public-projects/available-projects/`,
  GET_EXTERNAL_PUBLICLY_ASSIGNED_PROJECTS: (id?: number) =>
    `${tenantApiBase()}/public-projects/publicly-assigned-projects/${id ? id : ""}`,
  GET_EXTERNAL_AVAILABLE_PROJECT_DETAILS: (id: number) =>
    `${tenantApiBase()}/public-projects/available-projects/${id}/`,
  GET_EXTERNAL_PUBLICLY_ASSIGNED_PROJECT_DETAILS: (id: number) =>
    `${tenantApiBase()}/public-projects/publicly-assigned-projects/${id}/`,
  ACCEPT_PUBLIC_PROJECT: () =>
    `${tenantApiBase()}/public-projects/publicly-assigned-projects/`,
  PUBLIC_PROJECT_GROUPS: () =>
    `${tenantApiBase()}/public-projects/project-groups/`,
  START_EXTERNAL_GROUP_PROJECT: (projectId: number) =>
    `${tenantApiBase()}/public-projects/start-group-project/${projectId}/`,

  CREATE_EXTERNAL_PROJECT: (tenant: string) =>
    `${tenantApiBase(tenant)}/projects/create_projects/`,
  UPDATE_EXTERNAL_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/projects/create_projects/${projectId}/`,
  DELETE_EXTERNAL_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/projects/delete/${projectId}`,
  PUBLISH_EXTERNAL_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/projects/publish-projects/${projectId}/`,
  GET_EXTERNAL_PROJECT_PROGRESS: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/projects/project-progress/${projectId}/`,
  GET_CLIENT_SCOPES: (tenant: string) =>
    `${tenantApiBase(tenant)}/skills/client-scope/`,
  GET_CLIENTS_BY_SCOPE: (tenant: string) =>
    `${tenantApiBase(tenant)}/skills/view-clients-scope/`,
  GET_INTERNAL_PROJECT_TASKS: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/project-milestones/?project=${projectId}`,
  ASSIGN_TASK_TO_USER: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/assign-tasks/`,
  REMOVE_TASK_FROM_USER: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/remove-tasks/`,
  ADD_PUBLIC_IMAGE_RESOURCE: () =>
    `${tenantApiBase()}/clients/public-image-resources/`,
  GET_PUBLIC_IMAGE_RESOURCE: (userId: string, pageSize?: number) =>
    `${tenantApiBase()}/clients/public-image-resources/?user=${userId}${pageSize ? `&page_size=${pageSize}` : ''}`,
  GET_BOARDS: (tenant: string, projectId?: number) =>
    `${tenantApiBase(tenant)}/departments/lms-board/${projectId ? `?project_id=${projectId}` : ''}`,
  GET_ELIGIBLE_USERS: (tenant: string, projectId: number, boardId?: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/eligible-users-for-internal-projects/?project_id=${projectId}${boardId ? `&student_enroll__board=${boardId}` : ''}`,
  ADD_USERS_TO_PROJECT: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/add-students-and-teachers-to-internal-school-projects/`,
  DELETE_USERS_FROM_PROJECT: (tenant: string, projectId: string | number) =>
    `${tenantApiBase(tenant)}/assigned-projects/candidates/reject/${projectId}`,
  GET_CURRENT_PROJECT_MEMBERS: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/eligible-users/?project_id=${projectId}`,
  START_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/take-project-to-in-progress/${projectId}/`,
  COMPLETE_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/take-project-to-completed/${projectId}/`,
  REOPEN_PROJECT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/take-project-to-in-progress/${projectId}/`,
  FORUM: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/forum/`,
  FORUM_DETAIL: (tenant: string, forumId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/forum/${forumId}/`,
  FORUM_COMMENT: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/forum-comments/`,
  FORUM_COMMENT_DETAIL: (tenant: string, commentId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/forum-comments/${commentId}/`,
  PROJECT_NOTES: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/notes/`,
  PROJECT_NOTES_DETAIL: (tenant: string, noteId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/notes/${noteId}/`,
  PROJECT_DOCS: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/docs/`,
  PROJECT_DOCS_DETAIL: (tenant: string, id: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/docs/${id}/`,
  PROJECT_DOCS_COMMENTS: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/docs-comments/`,
  PROJECT_DOCS_COMMENTS_DETAIL: (tenant: string, id: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/docs-comments/${id}/`,
  FILTER_SKILLS: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/skill-framework/filter_skills/`,
  INTERNAL_PROJECT_SKILLS: (tenant: string) =>
    `${tenantApiBase(tenant)}/departments/internal-school-project-skills/`,
  INTERNAL_PROJECT_SKILL_DETAIL: (tenant: string, id: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-project-skills/${id}/`,
  REQUIRED_EVALUATION: (tenant: string, id: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/required-evaluation/${id}/`,
  EVALUATE_PEERS: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/evaluate-peers/`,
  GET_CLIENT_TEMPLATE: (tenant: string) =>
    `${tenantApiBase(tenant)}/client/template`,
  USER_SKILL_PERSONAL_PROFILE: (tenant: string, userId: string) =>
    `${tenantApiBase(tenant)}/users/user-skill-rating/personal_profile_update/?user=${encodeURIComponent(userId)}`,
  USER_SKILL_SELF_REVIEWED: (tenant: string, userId: string) =>
    `${tenantApiBase(tenant)}/users/user-skill-rating/self_reviewed/?user=${encodeURIComponent(userId)}`,
  INDIVIDUAL_PROGRESS: (tenant: string, projectId: number, userId: string, schema: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/individual-progress/?project_id=${projectId}&user_id=${encodeURIComponent(userId)}&evaluation_schema=${encodeURIComponent(schema)}`,
  INDIVIDUAL_PROGRESS_PER_EVALUATION: (tenant: string, projectId: number, userId: string, schema: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/individual-progress-per-evaluation/?project_id=${projectId}&user_id=${encodeURIComponent(userId)}&schema_name=${encodeURIComponent(schema)}`,
  PROJECT_EVALUATION_REPORT: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/project-evaluation-report/?id=${projectId}`,
  EXTERNAL_FORUM: () =>
    `${tenantApiBase()}/public-projects/public-forum/`,
  EXTERNAL_FORUM_DETAIL: (forumId: number) =>
    `${tenantApiBase()}/public-projects/public-forum/${forumId}/`,
  EXTERNAL_FORUM_COMMENT: () =>
    `${tenantApiBase()}/public-projects/public-comments/`,
  EXTERNAL_FORUM_COMMENT_DETAIL: (commentId: number) =>
    `${tenantApiBase()}/public-projects/public-comments/${commentId}/`,
  EXTERNAL_NOTES: () =>
    `${tenantApiBase()}/public-projects/public-notes/`,
  EXTERNAL_NOTES_DETAIL: (noteId: number) =>
    `${tenantApiBase()}/public-projects/public-notes/${noteId}/`,
  EXTERNAL_DOCS: () =>
    `${tenantApiBase()}/public-projects/public-docs/`,
  EXTERNAL_DOCS_DETAIL: (docId: number) =>
    `${tenantApiBase()}/public-projects/public-docs/${docId}/`,
  EXTERNAL_DOCS_COMMENTS: () =>
    `${tenantApiBase()}/public-projects/docs-comments/`,
  EXTERNAL_DOCS_COMMENTS_DETAIL: (id: number) =>
    `${tenantApiBase()}/public-projects/docs-comments/${id}/`,
  EXTERNAL_PROJECT_SKILLS: (tenant: string) =>
    `${tenantApiBase(tenant)}/projects/project-skills/`,
  EXTERNAL_PROJECT_SKILL_DETAIL: (tenant: string, id: number) =>
    `${tenantApiBase(tenant)}/projects/project-skills/${id}/`,
  START_EXTERNAL_EVALUATION: (id: number) =>
    `${tenantApiBase()}/public-projects/required-evaluation-public-project/${id}/`,
  EVALUATE_EXTERNAL_PEERS: () =>
    `${tenantApiBase()}/public-projects/evaluate-peers/`,
};

export const NOTIFICATION_ROUTES = {
  GET_NOTIFICATIONS: (tenant: string) =>
    `${tenantApiBase(tenant)}/notifications/user-notification/`,
  MARK_ALL_READ: (tenant: string) =>
    `${tenantApiBase(tenant)}/notifications/user-notification/mark_all_as_read/`,
};

export const CAREER_ROUTES = {
  PUBLIC_CAREERS: `${tenantApiBase()}/careers/careers/`,
  PUBLIC_CAREER_DETAIL: (id: number | string) =>
    `${tenantApiBase()}/careers/careers/${id}/`,
  CAREERS: (tenant: string) => `${tenantApiBase(tenant)}/careers/careers/`,
  CAREER_APPLICATIONS: `${tenantApiBase()}/careers/career-applications/`,
  CAREER_SUMMARY: (tenant: string) => `${tenantApiBase(tenant)}/users/career-details`,
};

export const PROJECT_RESOURCES_DETAILS = {
  ADD_OR_UPDATE_MILESTON: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/project-milestones/`,
  DELETE_MILESTONES: (tenant: string, milestoneId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/project-milestones/${milestoneId}`,
  UPDATE_MILESTONE: (tenant: string, milestoneId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/project-milestones/${milestoneId}/`,
  DUPLICATE_MILESTONE: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/duplicate-project-milestones/`,
  PROJECT_RESOURCES_GET: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-project-resources/?project=${projectId}`,
  PROJECT_RESOURCES_POST: (tenant: string) =>
    `${tenantApiBase(tenant)}/departments/internal-school-project-resources/`,
  PROJECT_RESOURCES_DELETE: (tenant: string, resourceId: number) =>
    `${tenantApiBase(tenant)}/departments/internal-school-project-resources/${resourceId}/`,
  CREATE_MILESTONE_TASK: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/milestones-tasks/`,
  UPDATE_MILESTONE_TASK: (tenant: string, taskId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/milestones-tasks/${taskId}/`,
  DELETE_MILESTONE_TASK: (tenant: string, taskId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/milestones-tasks/${taskId}/`,
  DUPLICATE_MILESTONE_TASK: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/duplicate-project-milestones-tasks/`,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ADD_TASK_DOCUMENT: (tenant: string, _: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-milestones-deliverables/`,
  GET_TASK_DOCUMENTS: (tenant: string, taskId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/milestones-tasks/${taskId}/documents/`,
  DELETE_TASK_DOCUMENT: (tenant: string, docId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/milestones-tasks-documents/${docId}/`,
  ADD_DELIVERABLE: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-milestones-deliverables/`,
  GET_MILESTONE_DELIVERABLES: (tenant: string, milestoneId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-milestones-deliverables/?milestone=${milestoneId}`,
  DELETE_MILESTONE_DELIVERABLE: (tenant: string, deliverableId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-milestones-deliverables/${deliverableId}/`,
  ADD_DELIVERABLE_COMMENT: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-deliverables-comments/`,
  DELETE_DELIVERABLE_COMMENT: (tenant: string, commentId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-deliverables-comments/${commentId}/`,
  GET_DELIVERABLE_COMMENTS: (tenant: string, deliverableId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-deliverables-comments/?deliverables=${deliverableId}`,
  UPDATE_DELIVERABLE_COMMENT: (tenant: string, commentId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/internal-deliverables-comments/${commentId}/`,
  // External project file resources (company only)
  EXTERNAL_PROJECT_FILE_RESOURCES_POST: (tenant: string) =>
    `${tenantApiBase(tenant)}/projects/project-file-resources/`,
  EXTERNAL_PROJECT_FILE_RESOURCES_DELETE: (tenant: string, resourceId: number) =>
    `${tenantApiBase(tenant)}/projects/project-file-resources/${resourceId}/`,
  // External (public-projects context) milestones/tasks
  EXTERNAL_MILESTONE_POST: () =>
    `${tenantApiBase()}/public-projects/public-milestones/`,
  EXTERNAL_MILESTONE_PATCH: (id: number) =>
    `${tenantApiBase()}/public-projects/public-milestones/${id}/`,
  EXTERNAL_MILESTONE_DELETE: (id: number) =>
    `${tenantApiBase()}/public-projects/public-milestones/${id}/`,
  EXTERNAL_TASK_POST: () =>
    `${tenantApiBase()}/public-projects/public-tasks/`,
  EXTERNAL_TASK_PATCH: (id: number) =>
    `${tenantApiBase()}/public-projects/public-tasks/${id}/`,
  EXTERNAL_TASK_DELETE: (id: number) =>
    `${tenantApiBase()}/public-projects/public-tasks/${id}/`,

  // External (public-projects context) deliverables/comments
  EXTERNAL_DELIVERABLES_GET: (milestoneId: number) =>
    `${tenantApiBase()}/public-projects/external-milestones-deliverables/?milestone=${milestoneId}`,
  EXTERNAL_DELIVERABLES_POST: () =>
    `${tenantApiBase()}/public-projects/external-milestones-deliverables/`,
  EXTERNAL_DELIVERABLES_DELETE: (id: number) =>
    `${tenantApiBase()}/public-projects/external-milestones-deliverables/${id}/`,

  EXTERNAL_COMMENTS_GET: (deliverableId: number) =>
    `${tenantApiBase()}/public-projects/external-deliverables-comments/?deliverables=${deliverableId}`,
  EXTERNAL_COMMENTS_POST: () =>
    `${tenantApiBase()}/public-projects/external-deliverables-comments/`,
  EXTERNAL_COMMENTS_PATCH: (id: number) =>
    `${tenantApiBase()}/public-projects/external-deliverables-comments/${id}/`,
  EXTERNAL_COMMENTS_DELETE: (id: number) =>
    `${tenantApiBase()}/public-projects/external-deliverables-comments/${id}/`,
};

/**
 * @NOTE These APIs are used on the assigned tabs only
 */
export const PROJECT_TEACHER_VIEW_APIS = {
  GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_TEACHER: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/view-my-projects/`,
  GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_TEACHER_ID: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/view-my-projects/${projectId}/`,
}

export const PROJECT_STUDENT_VIEW_APIS = {
  GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_STUDENT: (tenant: string) =>
    `${tenantApiBase(tenant)}/assigned-projects/view-my-projects/`,
  GET_INTERNAL_PROJECTS_DETAILED_APPLIED_BY_STUDENT_ID: (tenant: string, projectId: number) =>
    `${tenantApiBase(tenant)}/assigned-projects/view-my-projects/${projectId}/`,
}

export const COURSE_ROUTES = {
  GET_COURSES: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course/`,
  GET_COURSE_DETAIL: (tenant: string, courseId: string | number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course/${courseId}/`,
  DUPLICATE_COURSE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/duplicate-course/`,
  UPDATE_COURSE_STATUS: (tenant: string, courseId: string | number) =>
    `${API_HOST}/clients/${tenant}/api/departments/update-course-status/${courseId}/`,
  GET_BOARDS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-board/`,
  GET_BOARD_DETAIL: (tenant: string, boardId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-board/${boardId}/`,
  GET_BOARDS_TREE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-board-tree/`,
  GET_BOARD_TEACHERS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/board-teacher/`,
  PATCH_BOARD_TEACHER: (tenant: string, boardTeacherId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/board-teacher/${boardTeacherId}/`,
  DELETE_BOARD_TEACHER: (tenant: string, boardTeacherId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/board-teacher/${boardTeacherId}/`,
  UPDATE_BOARD: (tenant: string, boardId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-board/${boardId}/`,
  DELETE_BOARD: (tenant: string, boardId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-board/${boardId}/`,
  GET_USERS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/users/client-users-list/`,
  GET_COURSE_TEACHERS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-teacher/`,
  POST_BOARD_TEACHERS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/board-teacher/`,
  GET_COURSE_SECTIONS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-section/`,
  GET_LMS_REPO: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-repo/`,
  GET_COURSE_MARKING_SCHEME: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-marking-scheme/`,
  COURSE_MARKING_SCHEME_DETAIL: (tenant: string, markingSchemeId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-marking-scheme/${markingSchemeId}/`,
  GET_COURSE_STRUCTURES: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-structure/`,
  COURSE_STRUCTURE_DETAIL: (tenant: string, structureId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-structure/${structureId}/`,
  GET_COURSE_REPORT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-report/`,
  GET_ENROLL_STUDENTS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/enroll-student/`,
  PATCH_ENROLL_STUDENT: (tenant: string, enrollmentId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/enroll-student/${enrollmentId}/`,
  DELETE_ENROLL_STUDENT: (tenant: string, enrollmentId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/enroll-student/${enrollmentId}/`,
  GET_STUDENT_ATTENDANCE_PERCENTAGE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/students-attendance/students_attendance_percentage/`,
  GET_STUDENTS_ATTENDANCE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/students-attendance/`,
  EVALUATE_COURSE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/evaluate-course`,
  GET_YEARLY_COURSE_ATTENDANCE_REPORT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/students-attendance/yearly_course_attendance_report/`,
  UPDATE_STUDENTS_ATTENDANCE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/update-attendance/`,
  GET_REQUEST_LEAVE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/request-leave/`,
  CREATE_MULTI_COURSE_LEAVE_REQUEST: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/request-leave/request_leave_for_multiple_course/`,
  PATCH_ACCEPT_LEAVE: (tenant: string, leaveId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/accept-leave/${leaveId}/`,
  PATCH_COURSE_TEACHER: (tenant: string, teacherMappingId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-teacher/${teacherMappingId}/`,
  DELETE_COURSE_TEACHER: (tenant: string, teacherMappingId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-teacher/${teacherMappingId}/`,
  DELETE_COURSE_SECTION: (tenant: string, sectionId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-section/${sectionId}/`,
  PATCH_COURSE_SECTION: (tenant: string, sectionId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-section/${sectionId}/`,
  PATCH_LMS_REPO: (tenant: string, repoId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-repo/${repoId}/`,
  DELETE_LMS_REPO: (tenant: string, repoId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/lms-repo/${repoId}/`,
  CREATE_COURSE_SUB_STRUCTURE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-sub-structure/`,
  COURSE_SUB_STRUCTURE_DETAIL: (tenant: string, subStructureId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-sub-structure/${subStructureId}/`,
  CREATE_ASSIGNMENT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/assignment/`,
  GET_ASSIGNMENTS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/assignment/`,
  ASSIGNMENT_DETAIL: (tenant: string, assignmentId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/assignment/${assignmentId}/`,
  GET_STUDENT_SUBMISSIONS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-submission/`,
  CREATE_STUDENT_SUBMISSION: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-submission/`,
  CREATE_COURSE_FEEDBACK: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-feedback/`,
  COURSE_FEEDBACK_DETAIL: (tenant: string, feedbackId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-feedback/${feedbackId}/`,
  GENERATE_ASSIGNMENT_IDEAS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/generate-assignment-ideas`,
  PROCESS_COURSE_DOCUMENT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/ai/process-course-document/`,
  GENERATE_STUDY_MATERIALS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/ai/generate-study-materials/`,
  CREATE_STUDENT_GROUP: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-groups/`,
  STUDENT_GROUP_DETAIL: (tenant: string, groupId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-groups/${groupId}/`,
  GRADE_STUDENTS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/grade-students/`,
  GRADE_STUDENT_DETAIL: (tenant: string, gradeId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/grade-students/${gradeId}/`,
  GRADE_STUDENT_ASSIGNMENT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/grade-student-assignment`,
  GET_COURSE_ACQUIRED_MARKS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-acquired-marks/`,
  COURSE_ACQUIRED_MARKS_DETAIL: (tenant: string, acquiredMarkId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-acquired-marks/${acquiredMarkId}/`,
  CREATE_MULTIPLE_COURSE_ACQUIRED_MARKS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-acquired-marks/multiple_students/`,
  UPDATE_MULTIPLE_COURSE_ACQUIRED_MARKS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-acquired-marks/update_multiple_students_records/`,
  GET_STUDENT_COURSE_EVALUATION_REPORT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-course-evaluation-report/`,
  GET_STUDENT_COURSE_EVALUATION_REPORT_SELF: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-course-evaluation-report/self_reviewed/`,
  GET_STUDENT_COURSE_EVALUATION_REPORT_PEER: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-course-evaluation-report/peer_reviewed/`,
  GET_STUDENT_COURSE_EVALUATION_REPORT_MENTOR: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-course-evaluation-report/mentor_reviewed/`,
  COURSE_SKILL_EVALUATION_LISTING: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/course-skill-evaluation-listing/`,
  GET_STUDENT_ASSIGNMENT_REPORT: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/student-assignment-report/`,
  GRADING_STRUCTURE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/departments/grading-structure/`,
  GRADING_STRUCTURE_DETAIL: (tenant: string, gradingStructureId: number) =>
    `${API_HOST}/clients/${tenant}/api/departments/grading-structure/${gradingStructureId}/`,
};

export const CALENDAR_ROUTES = {
  GET_USER_ALL_TASKS: (tenant: string) =>
    `${tenantApiBase(tenant)}/calendar/user-all-tasks/`,
  ADD_EVENTS_WITH_INVITATIONS: (tenant: string) =>
    `${tenantApiBase(tenant)}/calendar/add-events-with-invitations/`,
  UPDATE_EVENT_WITH_INVITATIONS: (tenant: string, eventId: number) =>
    `${tenantApiBase(tenant)}/calendar/add-events-with-invitations/${eventId}/`,
  DELETE_EVENT_WITH_INVITATIONS: (tenant: string, eventId: number) =>
    `${tenantApiBase(tenant)}/calendar/add-events-with-invitations/${eventId}/`,
};

export const CLIENT_ROUTES = {
  GET_USER_ORGANIZATIONS: () => `${API_HOST}/api/users/user-client-details/`,

  GET_CLIENT_PLANS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/clients/payment_plans`,
  GET_CLIENT_STATS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/client/client-stats/`,
  GET_CLIENT_PROFILE: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/client/profile`,
  GET_CLIENT_SCOPE: (tenant: string, clientId: number) =>
    `${API_HOST}/clients/${tenant}/api/skills/view-clients-scope/${clientId}/`,
  REMOVE_CLIENT_SCOPE: (tenant: string, clientId: number) =>
    `${API_HOST}/clients/${tenant}/api/skills/view-clients-scope/${clientId}/remove_scope/`,
  GET_SCOPE_OPTIONS: (tenant: string) =>
    `${API_HOST}/clients/${tenant}/api/skills/scope/`,
};


export const BOARDS_ROUTES = {
  GET_ALL_BOARDS: (tenant: string, userId?: string) => `${API_HOST}/clients/${tenant}/api/departments/lms-board/?parent=&level=&board_enroll__student=&teacher_board__teacher=${userId}`,
  ADD_PROJECT_TO_BOARD: (tenant: string, projectId: number) => `${API_HOST}/clients/${tenant}/api/departments/internal-school-projects/${projectId}/`,
}
export const PUBLIC_ADMIN_ROUTES = {
  GET_SKILLS: `${API_HOST}/api/skills/skill/all`,
  CREATE_SKILL: `${API_HOST}/api/skills/skill/create`,
  SKILL_DETAIL: (skillId: number | string) =>
    `${API_HOST}/api/skills/skill/${skillId}/`,
  DELETE_SKILL: (skillId: number | string) =>
    `${API_HOST}/api/skills/skill/${skillId}/delete`,
  BULK_ADD_SKILLS: `${API_HOST}/api/skills/bulk-add-skills/`,
  GET_SCOPES: `${API_HOST}/api/skills/scope/`,
  SCOPE_DETAIL: (scopeId: number | string) =>
    `${API_HOST}/api/skills/scope/${scopeId}/`,
  GET_PRICING_PLANS: `${API_HOST}/api/clients/pricing_plans/`,
  GET_PAYMENT_PLANS: `${API_HOST}/api/clients/payment_plans/`,
  GET_ALL_CLIENTS: `${API_HOST}/api/clients/all`,
  GET_AVAILABLE_PROJECTS: `${API_HOST}/api/public-projects/available-projects/`,
  GET_CLIENT_SCOPE_OPTIONS: `${API_HOST}/api/skills/view-clients-scope/`,
  AVAILABLE_PROJECT_DETAIL: (projectId: number | string) =>
    `${API_HOST}/api/public-projects/available-projects/${projectId}/`,
  ASSIGN_PROJECT_DETAIL: (projectId: number | string) =>
    `${API_HOST}/api/public-projects/assign-projects/${projectId}/`,
  CLIENT_TEMPLATE: (clientId: number | string) =>
    `${API_HOST}/api/clients/${clientId}/template`,
  CLIENT_DETAILS: (clientId: number | string) =>
    `${API_HOST}/api/clients/${clientId}/details`,
  CLIENT_USERS: (clientId: number | string) =>
    `${API_HOST}/api/users/all-user-lists/?user_client_role__client=${clientId}`,
  CLIENT_USER_DELETE: (clientId: number | string, userId: string) =>
    `${API_HOST}/api/clients/${clientId}/users/delete/${encodeURIComponent(userId)}/`,
  PRICING_PLAN_DETAIL: (planId: number | string) =>
    `${API_HOST}/api/clients/pricing_plans/${planId}/`,
  GET_PRICING_MODULES: `${API_HOST}/api/clients/pricing_modules/`,
  PRICING_MODULE_DETAIL: (moduleId: number | string) =>
    `${API_HOST}/api/clients/pricing_modules/${moduleId}/`,
  GET_FEATURES: `${API_HOST}/api/clients/features_list/`,
  FEATURE_DETAIL: (featureId: number | string) =>
    `${API_HOST}/api/clients/features_list/${featureId}/`,
};
